import type {
	ComponentProps,
	KeyboardEvent as ReactKeyboardEvent,
	ReactNode,
} from "react"
import { forwardRef } from "react"

import { cn } from "@productlane/lib/src/cn"

import type { HighlightToken } from "./policy-token-highlight"
import { indentedNewline } from "../model/json-indent"
import {
	HIGHLIGHT_CARET,
	HIGHLIGHT_HIDDEN_TEXT,
	HIGHLIGHT_METRICS,
	HIGHLIGHT_RING_ERROR,
	HIGHLIGHT_RING_OK,
} from "./highlight-metrics"
import {
	FieldShell,
	HIGHLIGHT_VERTICAL,
	HighlightOverlay,
	PLAIN_TEXT_FIELD,
	useHighlightOverlay,
} from "./highlighted-field"

interface Props extends Omit<ComponentProps<"textarea">, "children"> {
	tokens: HighlightToken[]
	label?: ReactNode
	errorMessage?: ReactNode
	hint?: ReactNode
	wrapperClassName?: string
	/** Sits at the top trailing corner, for an action on the whole document. */
	action?: ReactNode
	/** Enter keeps the current line's indentation, as in a code editor. */
	autoIndent?: boolean
	/** Content-fit bounds, used when no explicit `rows` is passed. */
	minRows?: number
	maxRows?: number
}

// Written through the native setter so React's own onChange still fires, which is
// what keeps the controlled value and this edit in step.
function applyEdit(node: HTMLTextAreaElement, next: string, caret: number) {
	Object.getOwnPropertyDescriptor(
		HTMLTextAreaElement.prototype,
		"value",
	)?.set?.call(node, next)
	node.dispatchEvent(new Event("input", { bubbles: true }))
	node.setSelectionRange(caret, caret)
}

export const HighlightedTextarea = forwardRef<HTMLTextAreaElement, Props>(
	(
		{
			tokens,
			label,
			errorMessage,
			hint,
			wrapperClassName,
			action,
			autoIndent,
			className,
			value,
			rows,
			minRows = 2,
			maxRows = 14,
			...props
		},
		forwardedRef,
	) => {
		// Exact because wrap="off" below: one newline is one visual row.
		const lineCount =
			typeof value === "string" ? value.split("\n").length : minRows
		const fittedRows = rows ?? Math.min(Math.max(lineCount, minRows), maxRows)

		const { overlayRef, setRef, composing, syncProps } =
			useHighlightOverlay<HTMLTextAreaElement>({
				value,
				axis: "xy",
				forwardedRef,
			})

		const trailing = action ? "pr-9" : undefined

		const onKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
			props.onKeyDown?.(event)
			if (!autoIndent || event.defaultPrevented) return
			if (event.key !== "Enter" || event.shiftKey || event.metaKey) return

			const node = event.currentTarget
			event.preventDefault()
			const edit = indentedNewline(
				node.value,
				node.selectionStart ?? node.value.length,
				node.selectionEnd ?? node.value.length,
			)
			applyEdit(node, edit.value, edit.caret)
		}

		return (
			<FieldShell
				label={label}
				htmlFor={props.name}
				hint={hint}
				errorMessage={errorMessage}
				className={wrapperClassName}
			>
				<textarea
					{...props}
					{...PLAIN_TEXT_FIELD}
					{...syncProps}
					ref={setRef}
					value={value}
					rows={fittedRows}
					// Soft wrapping would need the overlay to break at the same points the
					// textarea does, which is where this technique falls apart.
					wrap="off"
					className={cn(
						"block w-full resize-none overflow-auto rounded-md bg-white ring-1 transition focus:outline-none dark:bg-transparent",
						HIGHLIGHT_METRICS,
						HIGHLIGHT_VERTICAL,
						trailing,
						!composing && HIGHLIGHT_HIDDEN_TEXT,
						HIGHLIGHT_CARET,
						errorMessage ? HIGHLIGHT_RING_ERROR : HIGHLIGHT_RING_OK,
						className,
					)}
					onKeyDown={onKeyDown}
				/>
				<HighlightOverlay
					shape="multi"
					tokens={tokens}
					placeholder={props.placeholder}
					composing={composing}
					overlayRef={overlayRef}
					className={trailing}
				>
					{/* A textarea reserves a final empty line; a span does not, so the last
					    line would drift up by one line height. */}
					{typeof value === "string" && value.endsWith("\n") ? "​" : null}
				</HighlightOverlay>
				{action ? (
					<span className="absolute right-1.5 top-1.5">{action}</span>
				) : null}
			</FieldShell>
		)
	},
)
